import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldType, SignerProfile, SigningField, Snapshot, SnapshotPageSize } from '../types/fields';
import { fingerprintPageSizes } from '../utils/pageSizeEpoch';
import { getPageSizeForField, isValidSnapshotField, toMediaPageSizesForSeed } from '../utils/signingFieldState';
import { restoreSnapshotFields, stripFieldsForSnapshot } from '../utils/snapshotRestore';
import { MAX_SIGNATURE_IMAGE_BYTES, ANNOTATION_TOOL_DEFAULTS, SIGNER_PROFILE_STORAGE_KEY, SNAPSHOTS_STORAGE_KEY, UI_PREFS_STORAGE_KEY, type WorkflowStage } from '../constants/signing';
import { usePdfSession } from '../hooks/usePdfSession';
import { usePdfDocument } from '../hooks/usePdfDocument';
import { useSignatureLibrary } from '../hooks/useSignatureLibrary';
import { useSigningFields } from '../hooks/useSigningFields';
import { PdfViewer } from './PdfViewer';
import { CommandBar } from '../shell/CommandBar';
import { AppShell } from '../shell/AppShell';
import { StatusBar } from '../shell/StatusBar';
import { ToolsIconRail } from './ToolsIconRail';
import { PagesPanel } from './PagesPanel';
import { RightInspector } from './RightInspector';
import { usePageOperations } from '../hooks/usePageOperations';
import { SignatureCreationFlow } from './SignatureCreationFlow';
import { fetchFreshDownloadUrl } from '../utils/api';
import { buildSignedDownloadFilename, saveSignedPdfFromUrl } from '../utils/downloadPdf';
import { getSourceDirectoryHandle } from '../utils/sourceFileAccess';
import { buildTextFieldCommitUpdates, isInlineEditableField, type EditorScreenBox } from './InlineFieldEditor';
import { measureTextFieldScreenBox } from '../utils/textFieldMeasure';
import { fieldTextLayoutMode } from '../utils/textLayout';
import { getDefaultFieldSize } from '../utils/coordinates';
import { cycleFieldSelectionAtPdfCenter } from '../utils/fieldSelection';
import type { FieldToolId } from './FieldToolsBar';
import {
  buildTextFormatUpdates,
  toggleBooleanField,
  type TextFormatPatch,
} from '../utils/textFormatActions';
import { isTextBasedFieldType } from '../utils/fontMapping';
import { buildTemplateBundle, downloadTemplateBundle, parseTemplateBundle } from '../utils/templateBundle';
import { getDocumentPrefsKey } from '../utils/documentPrefs';
import { validatePdfUploadFile } from '../utils/pdfUploadValidation';
import { getDefaultValueForType } from '../utils/signerProfile';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_PROFILE: SignerProfile = {
  name: '',
  company: '',
  title: '',
  email: '',
};

type CreationMode = 'draw' | 'type' | 'upload' | null;

export const SigningWorkspace: React.FC = () => {
  const pdf = usePdfSession();
  // Seed usePdfDocument with stable MEDIA fingerprints. syncViewerPageSizes swaps
  // width/height for 90/270° pages — normalizing here avoids a full pdf.js reload
  // when the viewer sync lands after upload.
  const mediaSeedFingerprint = useMemo(
    () =>
      pdf.pageCount > 0 ? fingerprintPageSizes(toMediaPageSizesForSeed(pdf.pageSizes)) : '',
    [pdf.pageCount, pdf.pageSizes]
  );
  const pdfDocSeed = useMemo(
    () =>
      pdf.pageCount > 0
        ? { pageCount: pdf.pageCount, pageSizes: toMediaPageSizesForSeed(pdf.pageSizes) }
        : null,
    [pdf.pageCount, mediaSeedFingerprint]
  );
  const sharedPdf = usePdfDocument(pdf.pdfFile, pdfDocSeed);
  const fields = useSigningFields(pdf.pageSizes, pdf.markSignedOutputStale);
  const fieldsRef = useRef(fields);
  const updateFieldsRef = useRef(fields.updateFields);
  fieldsRef.current = fields;
  updateFieldsRef.current = fields.updateFields;
  const library = useSignatureLibrary();
  const [profile, setProfile] = useState<SignerProfile>(DEFAULT_PROFILE);
  // PdfViewer memo ignores handler identity — keep profile fresh for click-to-place.
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const [creationMode, setCreationMode] = useState<CreationMode>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [currentTool, setCurrentTool] = useState<FieldType | 'select' | 'image' | null>('select');
  const [panMode, setPanMode] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const activePageIndexRef = useRef(activePageIndex);
  activePageIndexRef.current = activePageIndex;
  const imagePlacementRef = useRef<{
    pageIndex: number;
    pdfX: number;
    pdfY: number;
    renderScale: number;
  } | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  const templateImportRef = useRef<HTMLInputElement>(null);
  // Live thumbnails from PdfViewer (reused by PageNavigator for instant accurate previews; zero extra pdfjs work)
  const [liveThumbnails, setLiveThumbnails] = useState<Record<number, string>>({});
  const liveThumbnailsRef = useRef<Record<number, string>>({});
  const liveThumbnailsRafRef = useRef<number | null>(null);
  const handleLiveThumbnailsChange = useCallback((next: Record<number, string>) => {
    liveThumbnailsRef.current = next;
    // Coalesce: scrolling a 12-page doc can report 3–5 thumbs per frame — one
    // workspace re-render per frame instead of one per page (was cascading into
    // PagesPanel + StatusBar on every PdfPage canvas render).
    if (liveThumbnailsRafRef.current !== null) return;
    liveThumbnailsRafRef.current = requestAnimationFrame(() => {
      liveThumbnailsRafRef.current = null;
      setLiveThumbnails({ ...liveThumbnailsRef.current });
    });
  }, []);
  // Cancel any pending thumbnail RAF on unmount (it was only cancelled on
  // new-document reset, leaking a setState-after-unmount on teardown).
  useEffect(() => {
    return () => {
      if (liveThumbnailsRafRef.current !== null) {
        cancelAnimationFrame(liveThumbnailsRafRef.current);
        liveThumbnailsRafRef.current = null;
      }
    };
  }, []);
  const pendingPlacementWasArmed = useRef(false);
  // Bumped to cancel a deferred signature dialog open during rapid tool switching.
  const signatureDialogEpochRef = useRef(0);
  const dismissSignatureCreation = useCallback(() => {
    signatureDialogEpochRef.current += 1;
    setCreationMode(null);
  }, []);

  /** Open draw/type/upload immediately; cancels any deferred signature-type dialog. */
  const openSignatureMode = useCallback((mode: CreationMode) => {
    signatureDialogEpochRef.current += 1;
    setCreationMode(mode);
  }, []);
  // True only while pan mode was entered by *holding* Space (vs. toggled on via
  // 'p', the toolbar, or a restored UI preference). Lets the Space keyup handler
  // know whether to exit pan, so an incidental Space tap can't cancel a
  // deliberately persistent pan mode. Lives outside the keyboard effect so it
  // survives that effect's frequent rebinds.
  const spacePanActiveRef = useRef(false);
  const pendingPageRestoreRef = useRef<number | null>(null);
  const [inlineEditingFieldId, setInlineEditingFieldId] = useState<string | null>(null);
  const inlineEditDismissRef = useRef(false);
  const [inspectorBulkFieldIds, setInspectorBulkFieldIds] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  // Right inspector slide-over open state. Only meaningful at <768px;
  // on wider viewports the inspector is a static column regardless.
  // Initialized to false so the drawer is closed on first render.
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const toggleInspector = useCallback(() => {
    setInspectorOpen((prev) => !prev);
  }, []);

  // Auto-close the inspector drawer when the viewport widens past the
  // sm breakpoint (768px). The CSS hides the drawer on wider viewports
  // but the open state would otherwise persist invisibly; a later
  // resize back down would reopen a drawer the user thought they had
  // closed. Reset on the mql change.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(min-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setInspectorOpen(false);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // On narrow viewports, open the inspector drawer whenever the selected
  // field changes (placement, canvas tap, list pick). Closing the drawer
  // without deselecting leaves selectedFieldId unchanged, so the drawer
  // stays closed until the user picks another field.
  const prevSelectedFieldIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = fields.selectedFieldId;
    if (
      id &&
      id !== prevSelectedFieldIdRef.current &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767.98px)').matches
    ) {
      setInspectorOpen(true);
    }
    prevSelectedFieldIdRef.current = id;
  }, [fields.selectedFieldId]);
  // Track the viewer's current scale for display (StatusBar) and zoom-control callbacks.
  // undefined = let PdfViewer manage its own scale (initial / after localStorage restore).
  // non-undefined = explicitly set by the user via zoom controls (controlled mode).
  const [viewerScale, setViewerScale] = useState<number | undefined>(undefined);
  // Ref to the .editor-stage-viewer scroll container for fit-to-width/height calculations.
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const applyTextFormat = useCallback(
    (patch: TextFormatPatch, targetIds: string[]) => {
      // Apply to every targeted field as ONE undo step. Previously this issued N
      // separate updateField calls, so a bulk format (e.g. Bold across 5 selected
      // fields) pushed N history entries and required N Ctrl+Z to revert.
      const updatesById: Record<string, Partial<SigningField>> = {};
      const fState = fieldsRef.current;
      for (const id of targetIds) {
        const f = fState.fields.find((x) => x.id === id);
        if (!f || !isTextBasedFieldType(f.type)) continue;
        updatesById[id] = buildTextFormatUpdates(f, patch);
      }
      updateFieldsRef.current(updatesById);
    },
    []
  );

  // High-impact: Simple snapshot / history feature for before-after comparison
  // Now persisted to localStorage (survives reloads); optionally associated with documentName for multi-doc workflows.
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);

  // Compare mode: show a snapshot as "ghost" fields for visual comparison (without replacing current work)
  const [compareSnapshotId, setCompareSnapshotId] = useState<string | null>(null);

  // Snapshot save form state (replaces ugly blocking prompt() with clean inline Tailwind UI)
  const [snapshotSaveOpen, setSnapshotSaveOpen] = useState(false);
  const [snapshotDraftName, setSnapshotDraftName] = useState('');
  const [snapshotDraftGroup, setSnapshotDraftGroup] = useState('');

  const resetWorkspaceForNewDocument = useCallback(() => {
    fields.clearFields();
    liveThumbnailsRef.current = {};
    if (liveThumbnailsRafRef.current !== null) {
      cancelAnimationFrame(liveThumbnailsRafRef.current);
      liveThumbnailsRafRef.current = null;
    }
    setLiveThumbnails({});
    setCompareSnapshotId(null);
    setViewerScale(undefined);
    // Reset pan mode so it doesn't leak into the next document (the per-doc
    // prefs-restore effect early-returns when the new doc has no saved prefs).
    setPanMode(false);
    setCurrentTool('select');
    setInlineEditingFieldId(null);
    library.clearPendingPlacement();
    pendingPlacementWasArmed.current = false;
    setSnapshotSaveOpen(false);
    setSnapshotDraftName('');
    setSnapshotDraftGroup('');
  }, [fields, library]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SIGNER_PROFILE_STORAGE_KEY);
      if (raw) setProfile({ ...DEFAULT_PROFILE, ...JSON.parse(raw) });
    } catch {
      setProfile(DEFAULT_PROFILE);
    }
  }, []);

  // Skip the initial-mount write so we don't persist DEFAULT_PROFILE over the
  // stored profile before the read effect's setProfile has applied. Only writes
  // triggered by a real profile change (after the first render) hit localStorage.
  const profileWriteReadyRef = useRef(false);
  useEffect(() => {
    if (!profileWriteReadyRef.current) {
      profileWriteReadyRef.current = true;
      return;
    }
    localStorage.setItem(SIGNER_PROFILE_STORAGE_KEY, JSON.stringify(profile));
  }, [profile]);

  // Persist snapshots to localStorage (per-document tagging via documentName when available).
  // Loads on mount so UI shows persisted snapshots immediately after reload.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SNAPSHOTS_STORAGE_KEY);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      const normalized: Snapshot[] = parsed
        .filter((entry: any): entry is { id: string; name: string; fields: any[] } =>
          entry &&
          typeof entry.id === 'string' &&
          typeof entry.name === 'string' &&
          Array.isArray(entry.fields)
        )
        .map((entry: any) => ({
          id: entry.id,
          name: String(entry.name).trim().slice(0, 100) || 'Untitled snapshot',
          fields: entry.fields.filter(isValidSnapshotField),
          createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : undefined,
          documentName: typeof entry.documentName === 'string' ? entry.documentName : undefined,
          group: typeof entry.group === 'string' ? entry.group : undefined,
          pageSizes: Array.isArray(entry.pageSizes) ? entry.pageSizes : undefined,
        }));

      if (normalized.length > 0) {
        setSnapshots(normalized);
      }
    } catch {
      // Corrupt storage or quota issues: start fresh, non-fatal
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SNAPSHOTS_STORAGE_KEY, JSON.stringify(snapshots));
    } catch {
      // localStorage is full (often from large base64 signature images embedded in
      // snapshots). They still work in-memory this session, but warn the user so they
      // don't believe a snapshot was persisted when it won't survive a reload.
      pdf.setStatusMessage(
        '⚠️ Could not save snapshots locally — storage is full. They will be lost on reload. Remove large or signature-heavy snapshots to free space.'
      );
    }
  }, [snapshots, pdf.setStatusMessage]);

  // === UI PREFS PERSISTENCE (panMode + last active compareSnapshotId, per-document) ===
  // Restored on PDF load. For compareId we only re-activate if a snapshot with matching documentName still exists.
  const currentDocKey = getDocumentPrefsKey(pdf.uploadedFileId, pdf.originalFileName);
  const uiPrefsKey = `${UI_PREFS_STORAGE_KEY}:${currentDocKey}`;

  // Load pan + lastCompare on doc change (or initial mount if doc present)
  useEffect(() => {
    if (!pdf.originalFileName) return;
    try {
      const raw = localStorage.getItem(uiPrefsKey);
      if (!raw) return;
      const prefs = JSON.parse(raw) as {
        panMode?: boolean;
        lastCompareId?: string | null;
        activePageIndex?: number;
      };
      if (typeof prefs.panMode === 'boolean') {
        setPanMode(prefs.panMode);
      }
      if (typeof prefs.activePageIndex === 'number' && prefs.activePageIndex >= 0) {
        const clamped =
          pdf.pageCount > 0
            ? Math.min(prefs.activePageIndex, pdf.pageCount - 1)
            : prefs.activePageIndex;
        setActivePageIndex(clamped);
        pendingPageRestoreRef.current = clamped;
      }
      if (prefs.lastCompareId) {
        // Defer validation until snapshots are known; handled in next effect
      }
    } catch {
      // Ignore corrupt localStorage
    }
  }, [uiPrefsKey, pdf.originalFileName, pdf.uploadedFileId, pdf.pageCount]);

  // Scroll to last viewed page once the PDF pages are in the DOM
  useEffect(() => {
    if (pendingPageRestoreRef.current == null || !pdf.pdfFile || pdf.pageCount < 1) return;
    const idx = Math.min(pendingPageRestoreRef.current, pdf.pageCount - 1);
    const timer = window.setTimeout(() => {
      document.getElementById(`pdf-page-${idx}`)?.scrollIntoView({ behavior: 'auto', block: 'start' });
      pendingPageRestoreRef.current = null;
    }, 400);
    return () => window.clearTimeout(timer);
  }, [pdf.pdfFile, pdf.pageCount]);

  // Keep active page in range when page count shrinks (delete page, merge, etc.).
  useEffect(() => {
    if (pdf.pageCount < 1) {
      setActivePageIndex(0);
      return;
    }
    setActivePageIndex((idx) => Math.min(idx, pdf.pageCount - 1));
  }, [pdf.pageCount]);

  // Bulk selection must not survive a full field clear.
  useEffect(() => {
    if (fields.fields.length === 0) {
      setInspectorBulkFieldIds([]);
    }
  }, [fields.fields.length]);

  // Persist panMode + last viewed page when they change
  useEffect(() => {
    if (!pdf.originalFileName) return;
    try {
      const existing = (() => {
        try { return JSON.parse(localStorage.getItem(uiPrefsKey) || '{}'); } catch { return {}; }
      })();
      localStorage.setItem(
        uiPrefsKey,
        JSON.stringify({ ...existing, panMode, activePageIndex })
      );
    } catch {
      // Ignore localStorage quota errors
    }
  }, [uiPrefsKey, panMode, activePageIndex, pdf.originalFileName]);

  // Persist lastCompareId whenever it changes (lightweight)
  useEffect(() => {
    if (!pdf.originalFileName) return;
    try {
      const existing = (() => {
        try { return JSON.parse(localStorage.getItem(uiPrefsKey) || '{}'); } catch { return {}; }
      })();
      localStorage.setItem(uiPrefsKey, JSON.stringify({ ...existing, lastCompareId: compareSnapshotId }));
    } catch {
      // Ignore localStorage quota errors
    }
  }, [uiPrefsKey, compareSnapshotId, pdf.originalFileName]);

  // After snapshots or doc load: restore lastCompareId *only if* the snapshot's documentName matches current doc (or no doc tag)
  useEffect(() => {
    if (!compareSnapshotId && pdf.originalFileName) {
      try {
        const raw = localStorage.getItem(uiPrefsKey);
        if (!raw) return;
        const prefs = JSON.parse(raw) as { lastCompareId?: string | null };
        const candidate = prefs.lastCompareId;
        if (candidate) {
          const snap = snapshots.find(s => s.id === candidate);
          const docMatches = !snap?.documentName || snap.documentName === pdf.originalFileName;
          if (docMatches) {
            setCompareSnapshotId(candidate);
          }
        }
      } catch {
        // Ignore corrupt localStorage
      }
    }
  }, [snapshots, pdf.originalFileName, uiPrefsKey, compareSnapshotId]);

  const workflowStage: WorkflowStage = pdf.errorMessage
    ? 'error'
    : pdf.isUploading
    ? 'uploading'
    : pdf.isSigning
    ? 'applying'
    : library.pendingPlacement || (currentTool !== null && currentTool !== 'select')
    ? 'placing'
    : pdf.isSignedOutputStale
    ? 'stale'
    : pdf.signedDownloadUrl
    ? 'signed'
    : fields.fields.length > 0
    ? 'dirty'
    : pdf.pdfFile
    ? 'ready'
    : 'empty';

  const hasPdf = !!pdf.pdfFile;

  const pageOps = usePageOperations({
    uploadedFileId: pdf.uploadedFileId,
    pdfFile: pdf.pdfFile,
    pageSizes: pdf.pageSizes,
    fields: fields.fields,
    replaceFields: fields.replaceFields,
    // Activates the stale-session guard (M1): a page op that resolves after the
    // user loaded/cleared a new document bails instead of clobbering it.
    getLoadGeneration: pdf.getLoadGeneration,
    // One reorder = one undo entry: the optimistic remap skips history.
    replaceFieldsNoHistory: fields.replaceFieldsNoHistory,
    applyServerDocument: pdf.applyServerDocument,
    setPdfFileFromBlob: pdf.setPdfFileFromBlob,
    originalFileName: pdf.originalFileName,
    setStatusMessage: pdf.setStatusMessage,
    setErrorMessage: pdf.setErrorMessage,
  });
  const isBusy =
    pdf.isUploading || pdf.isSigning || pdf.isCancelling || pageOps.isPageOpRunning;
  // Note: in hosted (Vercel blob) mode the server fileIds from upload/merge/pages are short-lived.
  // The workspace + hooks keep pdf.pdfFile (the bytes) up to date via setPdfFileFromBlob after every op.
  // Apply can therefore use the direct bytes path (signPdf accepts the File).

  // ======================================================================
  // ZOOM CONTROL CALLBACKS
  // ======================================================================
  // viewerScale mirrors PdfViewer's effective zoom (reported via onScaleChange) and
  // is used only for the status-bar zoom % readout. The interactive zoom controls
  // (zoom-to-cursor, fit width/page/height) live in PdfViewer's own floating toolbar,
  // plus Ctrl/Cmd +/-/0 and Ctrl/Cmd+scroll — so PdfViewer owns zoom uncontrolled.

  const handleToolChange = useCallback(
    (tool: FieldToolId | 'image') => {
      library.clearPendingPlacement();
      pendingPlacementWasArmed.current = false;
      imagePlacementRef.current = null;

      if (tool === 'select') {
        dismissSignatureCreation();
        setCurrentTool('select');
        pdf.setStatusMessage('');
        return;
      }

      if (tool === 'signature-type') {
        setCurrentTool('select');
        pdf.setStatusMessage('');
        // Defer modal mount so rapid follow-up tool clicks can cancel before it blocks UI.
        const epoch = signatureDialogEpochRef.current + 1;
        signatureDialogEpochRef.current = epoch;
        window.setTimeout(() => {
          if (signatureDialogEpochRef.current === epoch) {
            setCreationMode('type');
          }
        }, 120);
        return;
      }

      // Dismiss any in-flight signature creation UI when leaving the signature tool.
      dismissSignatureCreation();
      setCurrentTool(tool);
      const hints: Record<string, string> = {
        text: 'Click the PDF to add text — then type or paste (Ctrl+V). Press Esc to cancel.',
        date: 'Click the PDF to place a date. Press Esc to cancel.',
        name: 'Click the PDF to place a printed name. Press Esc to cancel.',
        initials: 'Click the PDF to place initials. Press Esc to cancel.',
        checkbox: 'Click the PDF to place a checkbox. Press Esc to cancel.',
        image: 'Click the PDF to place an image (PNG/JPEG). Press Esc to cancel.',
        highlight: 'Click the PDF to place a highlight. Press Esc to cancel.',
        underline: 'Click the PDF to place an underline. Press Esc to cancel.',
        strikeout: 'Click the PDF to place a strikeout line. Press Esc to cancel.',
        rect: 'Click the PDF to place a rectangle. Press Esc to cancel.',
        ellipse: 'Click the PDF to place a circle. Press Esc to cancel.',
        cross: 'Click the PDF to place a cross mark. Press Esc to cancel.',
        'text-bubble': 'Click the PDF to place a text bubble. Press Esc to cancel.',
        ink: 'Drag on the PDF to draw a freehand stroke. Press Esc to cancel.',
      };
      pdf.setStatusMessage(hints[tool] || 'Click the PDF to place the field.');
    },
    [library, pdf, dismissSignatureCreation]
  );

  /** Enter click-to-place mode for the pending signature (same as the green Place button). */
  const armPendingPlacement = useCallback(
    (label: string) => {
      setCurrentTool('signature-upload');
      pendingPlacementWasArmed.current = true;
      pdf.setStatusMessage(`Click the PDF to place "${label}". Press Esc to cancel.`);
    },
    [pdf]
  );

  const handlePlacePendingSignature = useCallback(() => {
    if (!library.pendingPlacement) return;
    armPendingPlacement(library.pendingPlacement.label);
  }, [library.pendingPlacement, armPendingPlacement]);

  const handlePlaceField = useCallback((pageIndex: number, pdfX: number, pdfY: number, renderScale: number) => {
    if (pageOps.isPageOpRunning) return;
    if (library.pendingPlacement && pendingPlacementWasArmed.current) {
      const placed = fields.addField({
        type: 'signature-upload',
        pageIndex,
        pdfX,
        pdfY,
        renderScale,
        anchor: 'top',
        imageDataUrl: library.pendingPlacement.dataUrl,
        savedSignatureId: library.pendingPlacement.savedId,
      });
      if (!placed) return;
      const placedLabel = library.pendingPlacement.label;
      library.clearPendingPlacement();
      pendingPlacementWasArmed.current = false;
      setCurrentTool('select');
      pdf.setStatusMessage(`Placed ${placedLabel}. Drag or resize it if needed.`);
      fields.setSelectedFieldId(placed.id);
      return;
    }

    if (!currentTool || currentTool === 'select') return;

    if (currentTool === 'image') {
      imagePlacementRef.current = { pageIndex, pdfX, pdfY, renderScale };
      imageFileInputRef.current?.click();
      return;
    }

    // Annotation types: use ANNOTATION_TOOL_DEFAULTS for geometry/color props.
    // 'ink' is handled separately via onPlaceInk (freehand stroke capture in PdfPage).
    if (
      currentTool === 'highlight' ||
      currentTool === 'underline' ||
      currentTool === 'strikeout' ||
      currentTool === 'rect' ||
      currentTool === 'ellipse' ||
      currentTool === 'cross' ||
      currentTool === 'text-bubble'
    ) {
      const defaults = ANNOTATION_TOOL_DEFAULTS[currentTool];
      const placed = fields.addField({
        type: currentTool,
        pageIndex,
        pdfX,
        pdfY,
        renderScale,
        anchor: 'top',
        width: defaults.width,
        height: defaults.height,
        color: defaults.color,
        strokeColor: defaults.strokeColor,
        lineWidth: defaults.lineWidth,
        opacity: defaults.opacity,
        fillColor: defaults.fillColor,
        value: currentTool === 'text-bubble' ? 'Note' : undefined,
        fontSize: currentTool === 'text-bubble' ? 12 : undefined,
      });
      if (!placed) return;
      setCurrentTool('select');
      fields.setSelectedFieldId(placed.id);
      pdf.setStatusMessage(`Placed ${currentTool} annotation on page ${pageIndex + 1}.`);
      return;
    }

    // 'ink' does NOT use click-to-place; it fires via onPlaceInk after a drag stroke.
    // If for some reason handlePlaceField is invoked while tool is 'ink', do nothing.
    if (currentTool === 'ink') return;

    const placeValue = getDefaultValueForType(currentTool, profileRef.current);
    const { height: placeH } = getDefaultFieldSize(
      currentTool,
      renderScale
    );
    const placed = fields.addField({
      type: currentTool,
      pageIndex,
      pdfX,
      pdfY,
      renderScale,
      anchor:
        fieldTextLayoutMode(currentTool, {
          boxHeight: placeH,
          value: placeValue,
        }) === 'form-line'
          ? 'baseline'
          : 'top',
      value: placeValue,
      fontSize: currentTool === 'checkbox' ? undefined : 13,
      isChecked: currentTool === 'checkbox' ? false : undefined,
    });
    if (!placed) return;
    setCurrentTool('select');
    fields.setSelectedFieldId(placed.id);

    if (placed.type === 'text') {
      setInlineEditingFieldId(placed.id);
      pdf.setStatusMessage('Type or paste your text. Click away or press Enter when done.');
    } else {
      pdf.setStatusMessage(`Placed ${placed.type.replace(/-/g, ' ')} on page ${pageIndex + 1}.`);
    }
  }, [currentTool, fields, library, pdf, pageOps.isPageOpRunning]);

  const handleCancelInlineEdit = useCallback(() => {
    inlineEditDismissRef.current = true;
    setInlineEditingFieldId(null);
  }, []);

  const handleCommitInlineEdit = useCallback(
    (id: string, value: string, editorBox: Parameters<typeof buildTextFieldCommitUpdates>[2]) => {
      const field = fields.fields.find((f) => f.id === id);
      if (field) {
        fields.updateField(id, buildTextFieldCommitUpdates(field, value, editorBox));
      }
      setInlineEditingFieldId(null);
    },
    [fields]
  );

  /** Persist the open inline editor's textarea value before unmounting it. */
  const commitPendingInlineEdit = useCallback(
    (editingId: string) => {
      const el = document.querySelector('.inline-field-editor') as HTMLTextAreaElement | null;
      if (!el) {
        setInlineEditingFieldId(null);
        return;
      }

      const field = fields.fields.find((f) => f.id === editingId);
      if (!field || !isInlineEditableField(field)) {
        setInlineEditingFieldId(null);
        return;
      }

      const raw = el.value;
      const value =
        field.type === 'date' || field.type === 'initials' ? raw.trim() : raw;

      const computedFontSize = parseFloat(window.getComputedStyle(el).fontSize) || 13;
      const fieldFontSize = field.fontSize ?? (field.type === 'signature-type' ? 16 : 13);
      const displayScale = computedFontSize / fieldFontSize;
      const padX = 4 * displayScale;
      const measured = measureTextFieldScreenBox(field, value, displayScale);
      const editorBox: EditorScreenBox = {
        width: Math.ceil(Math.max(measured.width, el.scrollWidth + padX * 2)),
        height: Math.ceil(Math.max(measured.height, el.scrollHeight + 4)),
        displayScale,
      };

      handleCommitInlineEdit(editingId, value, editorBox);
    },
    [fields, handleCommitInlineEdit]
  );

  const handleFieldSelect = useCallback(
    (id: string | null) => {
      if (inlineEditingFieldId && id !== inlineEditingFieldId) {
        commitPendingInlineEdit(inlineEditingFieldId);
      }
      fields.setSelectedFieldId(id);
    },
    [fields, inlineEditingFieldId, commitPendingInlineEdit]
  );

  const handleLivePageSizes = useCallback(
    (
      liveSizes: Array<{ width: number; height: number; rotation?: number }>,
      epoch: number
    ) => {
      pdf.syncViewerPageSizes(liveSizes, epoch);
    },
    [pdf.syncViewerPageSizes]
  );

  const handleDuplicateToAllPages = useCallback(
    (id: string) => {
      const placed = fields.duplicateFieldToAllPages(id);
      pdf.setStatusMessage(
        placed > 0
          ? `Copied field to ${placed} other page${placed === 1 ? '' : 's'}.`
          : 'No other pages to copy this field to.'
      );
    },
    [fields, pdf]
  );

  /**
   * Called by PdfViewer/PdfPage after the user finishes a freehand 'ink' stroke.
   * points[] are already in PDF coordinate space (bottom-left origin).
   * We compute the bounding box here to store as the field's x/y/width/height.
   */
  const handlePlaceInk = useCallback(
    (pageIndex: number, points: Array<{ x: number; y: number }>, renderScale: number) => {
      if (pageOps.isPageOpRunning || points.length < 1) return;
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const defaults = ANNOTATION_TOOL_DEFAULTS.ink;
      const placed = fields.addField({
        type: 'ink',
        pageIndex,
        pdfX: minX,
        pdfY: maxY, // top of bounding box (PDF coords: top = max y)
        renderScale,
        anchor: 'top',
        width: Math.max(10, maxX - minX),
        height: Math.max(10, maxY - minY),
        points,
        strokeColor: defaults.strokeColor,
        lineWidth: defaults.lineWidth,
      });
      if (!placed) return;
      setCurrentTool('select');
      fields.setSelectedFieldId(placed.id);
      pdf.setStatusMessage(`Placed ink stroke on page ${pageIndex + 1}.`);
    },
    [fields, pdf, pageOps.isPageOpRunning]
  );

  // Keyboard shortcuts are defined later (after all callbacks) to avoid TDZ and for robustness.
  // See the second useEffect near the end of the component for the full global handler.

  // ========================================================================
  // DRAG-AND-DROP A PDF ANYWHERE + CTRL+V PASTE OF A PDF FILE
  // ========================================================================
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  /** Open a PDF that arrived via drop or clipboard — mirrors CommandBar onFileSelected. */
  const openExternalPdf = useCallback(
    async (file: File, options?: { ensurePdfExtension?: boolean }) => {
      if (pdf.isUploading || pdf.isSigning || pdf.isCancelling || pageOps.isPageOpRunning) {
        pdf.setStatusMessage('Please wait for the current operation to finish.');
        return;
      }
      // Clipboard files can carry no/odd names; loadPdf requires a .pdf extension.
      const named =
        options?.ensurePdfExtension && !file.name.toLowerCase().endsWith('.pdf')
          ? new File([file], `${file.name || 'pasted-document'}.pdf`, {
              type: 'application/pdf',
            })
          : file;
      const validationError = validatePdfUploadFile(named);
      if (validationError) {
        pdf.setErrorMessage(validationError);
        return;
      }
      resetWorkspaceForNewDocument();
      await pdf.loadPdf(named, null);
    },
    [resetWorkspaceForNewDocument, pdf, pageOps.isPageOpRunning]
  );

  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragOver(true);
    };
    // preventDefault on dragover is required for the drop event to fire at all.
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      dragDepthRef.current = 0;
      setIsDragOver(false);
      if (!hasFiles(e)) return;
      e.preventDefault();
      const dropped = Array.from(e.dataTransfer?.files ?? [])[0];
      if (!dropped) return;
      const validationError = validatePdfUploadFile(dropped);
      if (validationError) {
        pdf.setErrorMessage(validationError);
        return;
      }
      void openExternalPdf(dropped);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [openExternalPdf, pdf]);

  // Ctrl+V with a PDF file on the clipboard opens it. Text pastes (and pastes into
  // inputs) are untouched — we only act when an actual application/pdf file is present.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (creationMode) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (!file) continue;
        // Clipboard file items often expose MIME on DataTransferItem, not on File.
        const isPdf =
          item.type === 'application/pdf' ||
          file.type === 'application/pdf' ||
          file.name.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          e.preventDefault();
          void openExternalPdf(file, { ensurePdfExtension: true });
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [openExternalPdf, creationMode]);

  const handleDownload = useCallback(async () => {
    if (!pdf.signedFileId || isDownloading) return;
    if (pdf.isSignedOutputStale) {
      pdf.setStatusMessage('Re-apply signatures before downloading.');
      return;
    }

    const filename = buildSignedDownloadFilename(pdf.originalFileName, pdf.signedOutputName);
    setIsDownloading(true);
    pdf.setStatusMessage('Choose where to save your signed PDF…');

    try {
      // Fetch the signed file URL and resolve the source folder handle in parallel;
      // these are independent network/permission round-trips and previously ran serially
      // (3 awaits = ~3x the latency of the critical path).
      const [downloadUrl, startInDirectory] = await Promise.all([
        fetchFreshDownloadUrl(pdf.signedFileId, filename),
        getSourceDirectoryHandle(pdf.getSourceFileHandle()),
      ]);
      const result = await saveSignedPdfFromUrl(downloadUrl, filename, {
        startInDirectory,
      });
      const where =
        result.method === 'picker'
          ? startInDirectory
            ? 'Saved (Save dialog opened in your source PDF folder)'
            : 'Saved to the location you chose'
          : 'Saved using your browser download folder';
      pdf.setStatusMessage(`${where}: ${result.filename}`);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        pdf.setStatusMessage('Download cancelled.');
        return;
      }
      pdf.setStatusMessage(
        err instanceof Error ? err.message : 'Download failed. Try Apply again, then download.'
      );
    } finally {
      setIsDownloading(false);
    }
  }, [
    isDownloading,
    pdf.isSignedOutputStale,
    pdf.originalFileName,
    pdf.signedFileId,
    pdf.signedOutputName,
    pdf.setStatusMessage,
    pdf.getSourceFileHandle,
  ]);

  // Snapshot functions (high-impact for WYSIWYG confidence)
  // Snapshots are now loaded/saved via localStorage useEffects above. Survive page reloads.
  // save UI uses clean inline non-blocking form (no prompt()).
  const initiateSaveSnapshot = useCallback(() => {
    const defaultName = `Snapshot ${snapshots.length + 1}`;
    const doc = pdf.originalFileName || '';
    setSnapshotDraftName(defaultName);
    setSnapshotDraftGroup(doc); // auto-suggest / force group = current documentName (user can override)
    setSnapshotSaveOpen(true);
  }, [snapshots.length, pdf.originalFileName]);

  const commitSaveSnapshot = useCallback((name: string, group?: string) => {
    const trimmedName = (name || '').trim().slice(0, 100) || `Snapshot ${snapshots.length + 1}`;
    const trimmedGroup = (group || '').trim() || undefined;

    const strippedFields = stripFieldsForSnapshot(fields.fields);
    const newSnapshot: Snapshot = {
      id: `snap-${Date.now()}`,
      name: trimmedName,
      fields: structuredClone(strippedFields),
      createdAt: new Date().toISOString(),
      documentName: pdf.originalFileName || undefined,
      group: trimmedGroup,
      pageSizes: pdf.pageSizes.length ? structuredClone(pdf.pageSizes) : undefined,
    };
    setSnapshots(prev => [...prev, newSnapshot]);
    pdf.setStatusMessage(
      `Saved snapshot: ${trimmedName}${trimmedGroup ? ` • ${trimmedGroup}` : ''} (note: drawn/uploaded signature images are not stored in snapshots)`
    );
    setSnapshotSaveOpen(false);
    setSnapshotDraftName('');
    setSnapshotDraftGroup('');
  }, [fields.fields, snapshots.length, pdf.pageSizes, pdf.setStatusMessage, pdf.originalFileName]);

  // "Save as new version of current doc" — auto-names + auto-groups by documentName, bypasses form
  const saveCurrentAsVersion = useCallback(() => {
    const doc = pdf.originalFileName || 'document';
    const sameDocCount = snapshots.filter(s => (s.documentName || '') === (pdf.originalFileName || '')).length + 1;
    const autoName = `${doc} v${sameDocCount}`;
    const autoGroup = pdf.originalFileName || undefined;

    const strippedFields = stripFieldsForSnapshot(fields.fields);
    const newSnapshot: Snapshot = {
      id: `snap-${Date.now()}`,
      name: autoName,
      fields: structuredClone(strippedFields),
      createdAt: new Date().toISOString(),
      documentName: pdf.originalFileName || undefined,
      group: autoGroup,
      pageSizes: pdf.pageSizes.length ? structuredClone(pdf.pageSizes) : undefined,
    };
    setSnapshots(prev => [...prev, newSnapshot]);
    pdf.setStatusMessage(`Saved version: ${autoName} (grouped; drawn/uploaded signature images not stored)`);
    setSnapshotSaveOpen(false);
    setSnapshotDraftName('');
    setSnapshotDraftGroup('');
  }, [fields.fields, snapshots, pdf.pageSizes, pdf.originalFileName, pdf.setStatusMessage]);

  const restoreFieldsFromTemplate = useCallback(
    (
      rawFields: SigningField[],
      label: string,
      // Accept null placeholders (imported templates keep them to preserve
      // page-index alignment); restoreSnapshotFields handles them.
      sourcePageSizes?: ReadonlyArray<SnapshotPageSize | null>
    ) => {
      const { fields: restored, dropped } = restoreSnapshotFields(
        rawFields,
        pdf.pageSizes,
        sourcePageSizes
      );
      // Contract #3: loadFields pushes history, fires onFieldsChanged, marks stale.
      fields.loadFields(restored);
      pdf.setStatusMessage(
        `Loaded layout: ${label}` +
          (dropped > 0 ? ` — ${dropped} field(s) didn't fit this document and were skipped` : '')
      );
    },
    [fields, pdf.pageSizes, pdf.setStatusMessage]
  );

  const loadSnapshot = useCallback((snapshot: Snapshot) => {
    if (!confirm(`Load snapshot "${snapshot.name}"? This will replace your current fields.`)) return;
    restoreFieldsFromTemplate(snapshot.fields, snapshot.name, snapshot.pageSizes);
  }, [restoreFieldsFromTemplate]);

  const exportFieldTemplate = useCallback(() => {
    if (!fields.fields.length) {
      pdf.setStatusMessage('Place fields before exporting a template.');
      return;
    }
    downloadTemplateBundle(
      buildTemplateBundle({
        name: `${pdf.originalFileName?.replace(/\.pdf$/i, '') || 'document'} layout`,
        // Strip imageDataUrl (like snapshot-save) so multi-MB base64 images don't
        // bloat the template file and biometric signature images don't leak into it.
        fields: stripFieldsForSnapshot(fields.fields),
        group: pdf.originalFileName || undefined,
        documentName: pdf.originalFileName || undefined,
        pageSizes: pdf.pageSizes.length ? pdf.pageSizes : undefined,
      })
    );
    pdf.setStatusMessage('Template exported as .signpdf-template.json');
  }, [fields.fields, pdf]);

  const importFieldTemplate = useCallback(
    async (file: File) => {
      try {
        // Size guard before reading/parsing — mirror the PDF upload cap so a huge
        // (or hostile) template file can't OOM the JSON parse. Templates are tiny.
        if (file.size > 25 * 1024 * 1024) {
          pdf.setErrorMessage('Template file is too large to import (limit 25 MB).');
          return;
        }
        const bundle = parseTemplateBundle(JSON.parse(await file.text()));
        // Strip imageDataUrl before persisting to the snapshots list so large
        // base64 images don't blow the localStorage quota / leak biometric images.
        const strippedFields = stripFieldsForSnapshot(bundle.fields);
        const snap: Snapshot = {
          id: uuidv4(),
          name: bundle.name,
          fields: strippedFields,
          group: bundle.group,
          documentName: bundle.documentName,
          createdAt: new Date().toISOString(),
          // Keep null placeholders so page-index alignment survives a later
          // re-restore from this snapshot (restoreSnapshotFields tolerates them).
          pageSizes: bundle.pageSizes as SnapshotPageSize[] | undefined,
        };
        setSnapshots((prev) => [snap, ...prev]);
        if (hasPdf) {
          restoreFieldsFromTemplate(bundle.fields, bundle.name, bundle.pageSizes);
        } else {
          pdf.setStatusMessage(`Imported template "${bundle.name}" — upload a PDF to apply fields.`);
        }
      } catch (err) {
        pdf.setErrorMessage(err instanceof Error ? err.message : 'Could not import template.');
      }
    },
    [hasPdf, pdf, restoreFieldsFromTemplate]
  );

  const deleteSnapshot = useCallback((id: string) => {
    setSnapshots(prev => prev.filter(s => s.id !== id));
    if (compareSnapshotId === id) setCompareSnapshotId(null);
  }, [compareSnapshotId]);

  const renameSnapshot = useCallback((id: string, newName: string) => {
    const trimmed = newName.trim().slice(0, 100);
    if (!trimmed) return;
    setSnapshots(prev =>
      prev.map(s => (s.id === id ? { ...s, name: trimmed } : s))
    );
  }, []);

  // Lightweight reorder + group support for Snapshots list (persisted automatically via snapshots useEffect)
  const reorderSnapshots = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setSnapshots((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const setSnapshotGroup = useCallback((id: string, group?: string) => {
    const trimmed = group?.trim();
    setSnapshots(prev =>
      prev.map(s => (s.id === id ? { ...s, group: trimmed || undefined } : s))
    );
  }, []);

  const togglePanMode = useCallback(() => {
    setPanMode(prev => {
      const next = !prev;
      if (next) {
        // When entering pan, exit any placement and dismiss orphan tool UI.
        dismissSignatureCreation();
        setCurrentTool('select');
        pendingPlacementWasArmed.current = false;
        library.clearPendingPlacement();
        pdf.setStatusMessage('');
      }
      return next;
    });
  }, [library, pdf, dismissSignatureCreation]);

  const compareSnapshot = snapshots.find(s => s.id === compareSnapshotId);
  const compareFields = compareSnapshot ? compareSnapshot.fields : null;

  const handleCompareSnapshot = useCallback((id: string) => {
    if (compareSnapshotId === id) {
      setCompareSnapshotId(null);
      pdf.setStatusMessage('Exited compare mode');
    } else {
      setCompareSnapshotId(id);
      const snap = snapshots.find(s => s.id === id);
      pdf.setStatusMessage(`Comparing to snapshot: ${snap?.name || ''}`);
    }
  }, [compareSnapshotId, snapshots, pdf]);

  const addBusinessPreset = useCallback(() => {
    if (!pdf.pdfFile) return;
    const pageIndex = pdf.pageCount > 0 ? pdf.pageCount - 1 : 0;
    const pageSize = getPageSizeForField({ pageIndex }, pdf.pageSizes);
    if (!pageSize) {
      pdf.setStatusMessage('Page metadata is not ready yet — try again in a moment.');
      return;
    }
    // Left margin clamped to stay on-page even for narrow/landscape pages (never negative).
    const x = Math.max(36, Math.min(72, pageSize.width - 240));

    // Stack the block downward in the lower portion of the page. addField derives each
    // field's real size itself (passing width/height does nothing), so we advance using
    // the ACTUAL placed box (placed.y is its bottom) to get even, non-overlapping gaps.
    let cursorTop = Math.min(pageSize.height - 40, 320);
    const place = (type: FieldType, value: string | undefined, fontSize: number | undefined) => {
      const placed = fields.addField({ type, pageIndex, pdfX: x, pdfY: cursorTop, renderScale: 1, value, fontSize });
      if (placed) cursorTop = placed.y - 9; // next item's top sits 9pt below this item's bottom
      return placed;
    };

    const activeProfile = profileRef.current;
    place('signature-type', activeProfile.name || 'Your Signature', 20);
    place('name', activeProfile.name || 'Printed Name', 12);
    place('text', activeProfile.title || 'Title', 12);
    place('text', activeProfile.company || 'Company Name', 12);
    place('date', formatTodayDate(), 12);

    const checkbox = fields.addField({ type: 'checkbox', pageIndex, pdfX: x, pdfY: cursorTop, renderScale: 1, isChecked: true });
    if (checkbox) {
      fields.addField({
        type: 'text',
        pageIndex,
        pdfX: x + checkbox.width + 6,
        pdfY: cursorTop,
        renderScale: 1,
        value: 'I confirm I have reviewed and accept this document.',
        fontSize: 10,
      });
    }
    pdf.setStatusMessage('Business preset added to the last page.');
  }, [fields, pdf]);

  // Large-file + memory hint computation (used by LeftRail). Uses hook helper when available.
  // Only produces non-empty tip for genuinely large/complex cases — zero extra UI for typical small PDFs.
  const memoryTip = useMemo(() => {
    if (!pdf.pdfFile && pdf.pageCount === 0) return '';
    const sizeMB = (pdf.fileSize || (pdf.pdfFile?.size ?? 0)) / (1024 * 1024);
    const nPages = pdf.pageCount || 1;
    const nFields = fields.fields.length;

    let imgBytes = 0;
    for (const f of fields.fields) {
      if (f.imageDataUrl) {
        const commaIdx = f.imageDataUrl.indexOf(',');
        const b64 = commaIdx >= 0 ? f.imageDataUrl.slice(commaIdx + 1) : f.imageDataUrl;
        imgBytes += Math.round(b64.length * 0.75);
      }
    }

    // Use hook-provided score for consistency if present
    const score = pdf.getComplexityScore
      ? pdf.getComplexityScore(nFields, fields.fields.map((f) => f.imageDataUrl))
      : Math.round(nPages * Math.max(nFields, 1) + imgBytes / 1024);

    if (nFields >= 30) {
      return `High field count (${nFields}). Use "Save Snapshot" frequently to avoid losing work.`;
    }
    if (imgBytes > 1.8 * 1024 * 1024) {
      return `Large signature images (~${(imgBytes / 1024 / 1024).toFixed(1)} MB total). Snapshots recommended.`;
    }
    if (sizeMB > 12 || (nPages > 15 && nFields > 8)) {
      return `Large document (${sizeMB.toFixed(1)} MB, ${nPages} pages). Snapshots help prevent rework on complex edits. (On hosted/Vercel: very large + image sigs may hit serverless payload/timeout limits — prefer local desktop version.)`;
    }
    if (score > 420) {
      return 'Complex layout detected — save snapshots before heavy changes. (Hosted: consider local version for multi-image signatures.)';
    }
    return '';
  }, [pdf.fileSize, pdf.pdfFile, pdf.pageCount, pdf.getComplexityScore, fields.fields]);

  // ========================================================================
  // FULL GLOBAL KEYBOARD SHORTCUTS (high-impact, works anywhere, even virtualized pages)
  // ========================================================================
  // - Always attached to window so shortcuts work regardless of focus/which PdfPage is mounted
  // - Guards against INPUT/TEXTAREA/contenteditable to prevent stealing keys while typing values
  // - Spacebar pan made robust (preventDefault even on repeats, clear status on release)
  // - Delete/Backspace, arrows now centralized here (removed from per-page PdfPage)
  // - Added clear-all, snapshot save 's', pan toggle 'p', '?' help
  const libraryRef = useRef(library);
  const currentToolRef = useRef(currentTool);
  const pdfRef = useRef(pdf);
  const inspectorOpenRef = useRef(inspectorOpen);
  const inspectorBulkFieldIdsRef = useRef(inspectorBulkFieldIds);
  const inlineEditingFieldIdRef = useRef(inlineEditingFieldId);
  const panModeRef = useRef(panMode);
  libraryRef.current = library;
  currentToolRef.current = currentTool;
  pdfRef.current = pdf;
  inspectorOpenRef.current = inspectorOpen;
  inspectorBulkFieldIdsRef.current = inspectorBulkFieldIds;
  inlineEditingFieldIdRef.current = inlineEditingFieldId;
  panModeRef.current = panMode;

  useEffect(() => {
    const isEditingInput = (e: KeyboardEvent): boolean => {
      const t = e.target as HTMLElement | null;
      if (!t) return false;
      const tag = (t.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      if (t.closest('input,textarea,select,[contenteditable="true"],[contenteditable=""]')) return true;
      return false;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // Escape: priority order — inline-edit > input bail > placement cancel > deselect
      if (event.key === 'Escape') {
        // (a) Cancel an active inline text edit first (parent dismiss ref prevents blur commit).
        if (inlineEditingFieldIdRef.current) {
          handleCancelInlineEdit();
          return;
        }
        // (b) If focus is inside an input/textarea, let the input handle its own Escape.
        if (isEditingInput(event)) return;
        // (c) Cancel an armed signature placement; show "Placement canceled." ONLY when one was active.
        const lib = libraryRef.current;
        if (lib.pendingPlacement || pendingPlacementWasArmed.current) {
          lib.clearPendingPlacement();
          pendingPlacementWasArmed.current = false;
          setCurrentTool('select');
          pdfRef.current.setStatusMessage('Placement canceled.');
          return;
        }
        // (c'') Exit any active click-to-place / draw tool before deselect or drawer close
        // (text, ink, annotations, etc.) — must run before deselect so Esc cancels placement
        // mode even when a field happens to be selected.
        const tool = currentToolRef.current;
        if (tool && tool !== 'select') {
          imagePlacementRef.current = null;
          setCurrentTool('select');
          pdfRef.current.setStatusMessage('');
          return;
        }
        // (c') Close the inspector slide-over drawer on narrow viewports.
        // Runs after placement-cancel so a user mid-placement does not
        // accidentally dismiss the drawer with Escape.
        if (inspectorOpenRef.current) {
          setInspectorOpen(false);
          return;
        }
        // (d) Deselect any selected field when not in placement mode.
        const fState = fieldsRef.current;
        if (fState.selectedFieldId) {
          fState.setSelectedFieldId(null);
        }
        return;
      }

      // Skip app shortcuts while user is typing in form fields (properties panel, search, etc.)
      if (isEditingInput(event)) {
        return;
      }

      // Inline edit open but focus left the textarea (stage click, toolbar, etc.):
      // Delete/Backspace cancels the edit first — mirrors Escape — instead of deleting
      // the selected field underneath or leaving a stale editor mounted.
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        inlineEditingFieldIdRef.current
      ) {
        event.preventDefault();
        handleCancelInlineEdit();
        return;
      }

      const fState = fieldsRef.current;

      // Undo / Redo (app-level; browser undo preserved inside inputs thanks to guard above)
      // Guard on !isBusy so undo/redo can't mutate field state mid page-operation.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !isBusy) {
        event.preventDefault();
        if (event.shiftKey) {
          fState.redo?.();
        } else {
          fState.undo();
        }
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y' && !isBusy) {
        event.preventDefault();
        fState.redo?.();
        return;
      }

      // Ctrl/Cmd+C = copy selected field to internal clipboard
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c' && fState.selectedFieldId && !isBusy) {
        event.preventDefault();
        if (fState.copyField(fState.selectedFieldId)) {
          pdfRef.current.setStatusMessage('Field copied. Press Ctrl+V on another page to paste.');
        }
        return;
      }

      // Ctrl/Cmd+V = paste copied field (PDF-file paste is handled by the paste listener)
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v' && fState.canPasteField() && !isBusy) {
        event.preventDefault();
        const clone = fState.pasteField(activePageIndexRef.current);
        if (clone) {
          pdfRef.current.setStatusMessage('Field pasted.');
        }
        return;
      }

      // Ctrl/Cmd+D = duplicate selected field; +Shift = copy it to every page
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd' && fState.selectedFieldId && !isBusy) {
        event.preventDefault();
        if (event.shiftKey) {
          const placed = fState.duplicateFieldToAllPages(fState.selectedFieldId);
          pdfRef.current.setStatusMessage(
            placed > 0
              ? `Copied field to ${placed} other page${placed === 1 ? '' : 's'}.`
              : 'No other pages to copy this field to.'
          );
        } else {
          const clone = fState.duplicateField(fState.selectedFieldId);
          if (clone) pdfRef.current.setStatusMessage('Field duplicated. Drag it into position.');
        }
        return;
      }

      // High-impact: Ctrl/Cmd + Shift + Delete/Backspace = Clear ALL fields (with confirm)
      if ((event.metaKey || event.ctrlKey) && event.shiftKey &&
          (event.key === 'Delete' || event.key.toLowerCase() === 'backspace') && !isBusy) {
        event.preventDefault();
        if (fState.fields.length > 0) {
          if (confirm(`Delete all ${fState.fields.length} fields? This cannot be undone.`)) {
            fState.clearFields();
            pdfRef.current.setStatusMessage('All fields cleared via shortcut.');
          }
        }
        return;
      }

      // Enter / F2 = inline-edit selected text field (easier than double-click prompt)
      if (
        (event.key === 'Enter' || event.key === 'F2') &&
        fState.selectedField &&
        isInlineEditableField(fState.selectedField) &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        setInlineEditingFieldId(fState.selectedField.id);
        return;
      }

      // Text formatting shortcuts (selected or bulk text fields)
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !isBusy) {
        const key = event.key.toLowerCase();
        const formatKeys = { b: 'bold', i: 'italic', u: 'underline' } as const;
        if (key in formatKeys) {
          const bulkTextIds = inspectorBulkFieldIdsRef.current.filter((id) => {
            const f = fState.fields.find((x) => x.id === id);
            return f && isTextBasedFieldType(f.type);
          });
          const targetIds =
            bulkTextIds.length > 0
              ? bulkTextIds
              : fState.selectedField && isTextBasedFieldType(fState.selectedField.type)
                ? [fState.selectedField.id]
                : [];
          const primary = targetIds[0]
            ? fState.fields.find((x) => x.id === targetIds[0])
            : null;
          if (primary) {
            event.preventDefault();
            applyTextFormat(
              toggleBooleanField(primary, formatKeys[key as keyof typeof formatKeys]),
              targetIds
            );
            return;
          }
        }
      }

      // Tab cycles through overlapping fields at the selected field's center.
      if (event.key === 'Tab' && fState.selectedFieldId && !event.shiftKey) {
        const f = fState.selectedField;
        if (f) {
          const nextId = cycleFieldSelectionAtPdfCenter(fState.fields, f);
          if (nextId && nextId !== f.id) {
            event.preventDefault();
            fState.setSelectedFieldId(nextId);
            return;
          }
        }
      }

      // Delete / Backspace = delete the globally selected field (works even if page virtualized/not in DOM)
      if ((event.key === 'Delete' || event.key === 'Backspace') && fState.selectedFieldId && !isBusy) {
        event.preventDefault();
        if (inlineEditingFieldIdRef.current === fState.selectedFieldId) {
          setInlineEditingFieldId(null);
        }
        fState.deleteField(fState.selectedFieldId);
        return;
      }

      // Arrow keys = nudge selected field (global, 1pt or 10pt w/ Shift). Prevents page scroll.
      if (fState.selectedFieldId && !isBusy && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        const f = fState.selectedField;
        if (!f) return;
        event.preventDefault();

        const step = event.shiftKey ? 10 : 1;
        let { x, y } = f;
        let dx = 0;
        let dy = 0;

        if (event.key === 'ArrowLeft') { x -= step; dx = -step; }
        if (event.key === 'ArrowRight') { x += step; dx = step; }
        if (event.key === 'ArrowUp') { y += step; dy = step; }   // PDF y grows upward from bottom
        if (event.key === 'ArrowDown') { y -= step; dy = -step; }

        const updates: Partial<typeof f> = { x, y };
        if (f.type === 'ink' && f.points?.length) {
          updates.points = f.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        }
        fState.updateField(f.id, updates);
        return;
      }

      // Spacebar = temporary pan (robust: prevent scroll, works on repeat, clear status on release)
      if (event.key === ' ' && hasPdf && !isBusy) {
        event.preventDefault();
        if (!event.repeat && !panModeRef.current) {
          spacePanActiveRef.current = true;
          setPanMode(true);
          pdfRef.current.setStatusMessage('Pan mode (hold Space to drag, release Space to exit)');
        }
      }

      // Letter shortcuts (only when not in inputs)
      const k = event.key.toLowerCase();
      if (k === 't' && !event.metaKey && !event.ctrlKey && hasPdf && !isBusy) {
        event.preventDefault();
        handleToolChange('text');
        return;
      }
      if (k === 'v' && !event.metaKey && !event.ctrlKey && hasPdf && !isBusy) {
        event.preventDefault();
        handleToolChange('select');
        return;
      }
      if (k === 's' && !event.metaKey && !event.ctrlKey && hasPdf && !isBusy) {
        event.preventDefault();
        initiateSaveSnapshot();
        return;
      }
      // Require no modifier so this doesn't swallow Ctrl/Cmd+P (browser print).
      if (k === 'p' && !event.metaKey && !event.ctrlKey && hasPdf && !isBusy) {
        event.preventDefault();
        togglePanMode();
        return;
      }
      // '/' focuses the saved signatures search input when visible (>4 sigs) and not typing in any input (guarded above)
      if (k === '/' && !event.shiftKey) {
        event.preventDefault();
        const sigSearch = document.querySelector<HTMLInputElement>('[data-signature-search="true"]');
        if (sigSearch) {
          sigSearch.focus();
          sigSearch.select();
        }
        return;
      }
      if (event.key === '?' || (event.shiftKey && k === '/')) {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      // Only exit pan on Space release if pan was *entered* by holding Space.
      // Pan toggled on via 'p', the toolbar button, or a restored UI preference
      // must survive an incidental Space tap (it owns panMode independently).
      if (event.key === ' ' && spacePanActiveRef.current) {
        spacePanActiveRef.current = false;
        if (panModeRef.current) {
          setPanMode(false);
          pdfRef.current.setStatusMessage('');
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [
    hasPdf,
    isBusy,
    initiateSaveSnapshot,
    togglePanMode,
    handleToolChange,
    applyTextFormat,
    handleCancelInlineEdit,
  ]);

  const handleImageFile = useCallback(
    (file: File | undefined) => {
      const pending = imagePlacementRef.current;
      if (!pending) return;

      if (!file) {
        imagePlacementRef.current = null;
        pdf.setStatusMessage(
          'Image placement cancelled. Click the PDF to place an image (PNG/JPEG). Press Esc to cancel.'
        );
        return;
      }

      const hasAllowedMime = /^image\/(png|jpeg|jpg)$/i.test(file.type);
      const hasAllowedExtension = /\.(png|jpe?g)$/i.test(file.name);
      if (!hasAllowedMime && !hasAllowedExtension) {
        pdf.setStatusMessage('Please choose a PNG or JPEG image.');
        return;
      }
      // Contract #6: reject oversized images before encoding to avoid a cryptic 413 at sign time.
      if (file.size > MAX_SIGNATURE_IMAGE_BYTES) {
        const limitMb = (MAX_SIGNATURE_IMAGE_BYTES / (1024 * 1024)).toFixed(0);
        const fileMb = (file.size / (1024 * 1024)).toFixed(1);
        pdf.setStatusMessage(
          `Image too large (${fileMb} MB). Please use an image under ${limitMb} MB.`
        );
        return;
      }

      imagePlacementRef.current = null;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        if (!/^data:image\/(png|jpeg|jpg);base64,/i.test(dataUrl)) {
          pdf.setStatusMessage('Could not read the image file. Try a different PNG or JPEG.');
          return;
        }
        const placed = fields.addField({
          type: 'image',
          pageIndex: pending.pageIndex,
          pdfX: pending.pdfX,
          pdfY: pending.pdfY,
          renderScale: pending.renderScale,
          anchor: 'top',
          imageDataUrl: dataUrl,
        });
        if (!placed) return;
        setCurrentTool('select');
        fields.setSelectedFieldId(placed.id);
        pdf.setStatusMessage('Image placed. Drag or resize if needed.');
      };
      reader.onerror = () => {
        pdf.setStatusMessage('Could not read the image file. Try a different PNG or JPEG.');
      };
      reader.readAsDataURL(file);
    },
    [fields, pdf]
  );

  const workflowDirty =
    fields.fields.length > 0 && !pdf.signedDownloadUrl;

  const handleDismissError = useCallback(() => {
    pdf.clearError();
    library.clearStorageError();
  }, [pdf.clearError, library.clearStorageError]);

  const activeErrorMessage = pdf.errorMessage || library.storageError;

  return (
    <AppShell
      inspectorOpen={inspectorOpen}
      onToggleInspector={toggleInspector}
      topBar={
      <CommandBar
        currentFileName={pdf.originalFileName}
        // Use the full isBusy (includes page operations) so CommandBar buttons
        // (Apply, signature creators, etc.) are disabled during page ops too.
        isBusy={isBusy}
        isPlacing={workflowStage === 'placing'}
        canPlace={!!library.pendingPlacement && !!pdf.pdfFile}
        hasPdf={!!pdf.pdfFile}
        hasFields={fields.fields.length > 0}
        canDownload={!!pdf.signedFileId && !pdf.isSignedOutputStale}
        isSignedOutputStale={pdf.isSignedOutputStale}
        onUploadError={pdf.setErrorMessage}
        onFileSelected={async (file, sourceHandle) => {
          const validationError = validatePdfUploadFile(file);
          if (validationError) {
            pdf.setErrorMessage(validationError);
            return;
          }
          resetWorkspaceForNewDocument();
          await pdf.loadPdf(file, sourceHandle ?? null);
        }}
        onCreateDrawSignature={() => openSignatureMode('draw')}
        onCreateTypeSignature={() => openSignatureMode('type')}
        onCreateUploadSignature={() => openSignatureMode('upload')}
        onPlacePendingSignature={handlePlacePendingSignature}
        onApply={() =>
          pdf.applySignatures(fields.fields, {
            signerProfile: profile,
            saveAuditManifest: true,
          })
        }
        onDownload={() => { void handleDownload(); }}
        isDownloading={isDownloading}
        onCleanup={() => {
          if (confirm('Delete all temporary PDFs from uploads and output?')) {
            resetWorkspaceForNewDocument();
            void pdf.clearLocalFiles();
          }
        }}
        onBusinessPreset={addBusinessPreset}
        onSaveSnapshot={initiateSaveSnapshot}
        canUndo={fields.canUndo}
        canRedo={fields.canRedo}
        onUndo={fields.undo}
        onRedo={fields.redo}
        panMode={panMode}
        onTogglePan={togglePanMode}
        onToggleInspector={toggleInspector}
        inspectorOpen={inspectorOpen}
        // Large-file UX + cancellation wiring (progress only renders during ops)
        isUploading={pdf.isUploading}
        isSigning={pdf.isSigning}
        uploadProgress={pdf.uploadProgress}
        signingProgress={pdf.signingProgress}
        isCancelling={pdf.isCancelling}
        onCancelUpload={pdf.cancelUpload}
        onCancelSigning={pdf.cancelSigning}
        fileSize={pdf.fileSize}
        // Annotation toolbar wiring
        annotationTool={
          currentTool === 'highlight' ||
          currentTool === 'underline' ||
          currentTool === 'strikeout' ||
          currentTool === 'rect' ||
          currentTool === 'ellipse' ||
          currentTool === 'cross' ||
          currentTool === 'text-bubble' ||
          currentTool === 'ink'
            ? currentTool
            : null
        }
        onAnnotationToolChange={(tool) => {
          if (tool === null) {
            dismissSignatureCreation();
            setCurrentTool('select');
            pdf.setStatusMessage('');
          } else {
            // Delegate to handleToolChange to get consistent status messages + library clearing
            handleToolChange(tool as FieldToolId | 'image');
          }
        }}
        annotationDisabled={!pdf.pdfFile || isBusy}
        currentTool={
          currentTool === 'select' || currentTool === 'image'
            ? currentTool
            : currentTool && currentTool !== 'signature-type'
              ? (currentTool as FieldToolId)
              : 'select'
        }
        onToolChange={handleToolChange}
        toolsDisabled={!pdf.pdfFile || isBusy}
      />
      }
      toolsDock={
        <ToolsIconRail
          activeTool={
            currentTool === 'select' || currentTool === 'image'
              ? currentTool
              : currentTool && currentTool !== 'signature-type'
                ? (currentTool as FieldToolId)
                : 'select'
          }
          onToolChange={handleToolChange}
          disabled={!pdf.pdfFile || isBusy}
        />
      }
      stage={
        <>
          <div className="editor-stage-viewer" ref={viewerContainerRef}>
      {/* Modern inline snapshot save UI (replaces blocking prompt; non-modal, Tailwind-styled, auto groups by doc) */}
      {snapshotSaveOpen && (
        <div className="mx-4 mt-2 rounded-xl border border-accent-muted bg-accent-soft dark:bg-accent-soft dark:border-accent p-3 text-sm shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] font-medium text-accent dark:text-accent-muted mb-0.5">Snapshot name</div>
              <input
                type="text"
                value={snapshotDraftName}
                onChange={(e) => setSnapshotDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitSaveSnapshot(snapshotDraftName, snapshotDraftGroup);
                  } else if (e.key === 'Escape') {
                    setSnapshotSaveOpen(false);
                    setSnapshotDraftName('');
                    setSnapshotDraftGroup('');
                  }
                }}
                className="w-full text-sm px-2.5 py-1 rounded border border-accent-muted dark:border-accent bg-white dark:bg-slate-900 focus:outline-none focus:border-accent"
                placeholder="Snapshot name"
                autoFocus
              />
            </div>
            <div className="min-w-[160px]">
              <div className="text-[10px] font-medium text-accent dark:text-accent-muted mb-0.5 flex items-center gap-1">
                Group <span className="opacity-60">(optional)</span>
              </div>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={snapshotDraftGroup}
                  onChange={(e) => setSnapshotDraftGroup(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitSaveSnapshot(snapshotDraftName, snapshotDraftGroup);
                    if (e.key === 'Escape') { setSnapshotSaveOpen(false); setSnapshotDraftName(''); setSnapshotDraftGroup(''); }
                  }}
                  className="flex-1 text-sm px-2.5 py-1 rounded border border-accent-muted dark:border-accent bg-white dark:bg-slate-900 focus:outline-none focus:border-accent"
                  placeholder="e.g. contract.pdf"
                />
                {pdf.originalFileName && (
                  <button
                    onClick={() => setSnapshotDraftGroup(pdf.originalFileName || '')}
                    className="text-[10px] px-2 py-1 rounded border border-accent-muted hover:bg-accent-muted dark:border-accent dark:hover:bg-accent-soft-strong transition-colors"
                    title="Set group to current document name"
                  >
                    Use doc
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => commitSaveSnapshot(snapshotDraftName, snapshotDraftGroup)}
                className="px-3 py-1 rounded-lg bg-accent hover:bg-accent active:bg-accent text-white text-xs font-semibold transition-colors"
              >
                Save snapshot
              </button>
              <button
                onClick={() => { setSnapshotSaveOpen(false); setSnapshotDraftName(''); setSnapshotDraftGroup(''); }}
                className="px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              {pdf.originalFileName && fields.fields.length > 0 && (
                <button
                  onClick={saveCurrentAsVersion}
                  className="px-2.5 py-1 rounded-lg bg-state-success hover:bg-state-success active:bg-state-success text-white text-xs font-semibold transition-colors"
                  title="Auto-name + group by current document as a new version (e.g. contract.pdf v3)"
                >
                  Save as new version of current doc
                </button>
              )}
            </div>
          </div>
          <div className="mt-1.5 text-[10px] text-accent-muted dark:text-accent-muted">
            New snapshots from “{pdf.originalFileName || 'this doc'}” default to that group. Override freely or use quick version button.
          </div>
        </div>
      )}

          <PdfViewer
            file={pdf.pdfFile}
            fileId={pdf.uploadedFileId}
            sharedPdf={sharedPdf}
            fields={fields.fields}
            selectedFieldId={fields.selectedFieldId}
            currentTool={currentTool}
            onPlaceField={handlePlaceField}
            onFieldUpdate={fields.updateField}
            onFieldSelect={handleFieldSelect}
            onFieldDelete={fields.deleteField}
            inlineEditingFieldId={inlineEditingFieldId}
            onStartInlineEdit={setInlineEditingFieldId}
            onCommitInlineEdit={handleCommitInlineEdit}
            onCancelInlineEdit={handleCancelInlineEdit}
            inlineEditDismissRef={inlineEditDismissRef}
            onLivePageSizes={handleLivePageSizes}
            onLiveThumbnailsChange={handleLiveThumbnailsChange}
            documentName={pdf.originalFileName}
            compareFields={compareFields}
            compareLabel={compareSnapshot?.name || null}
            onExitCompare={() => {
              setCompareSnapshotId(null);
              pdf.setStatusMessage('Exited compare mode');
            }}
            panMode={panMode}
            onTogglePan={togglePanMode}
            onVisiblePageChange={(pageIndex) => {
              activePageIndexRef.current = pageIndex;
              setActivePageIndex(pageIndex);
            }}

            onDuplicateToAllPages={handleDuplicateToAllPages}
            onScaleChange={setViewerScale}
            onPlaceInk={handlePlaceInk}
            activePageIndex={activePageIndex}
          />
          </div>
          <PagesPanel
            file={pdf.pdfFile}
            pageCount={pdf.pageCount}
            liveThumbnails={liveThumbnails}
            sharedPdfDoc={sharedPdf.doc}
            disabled={!pdf.pdfFile || isBusy}
            activePageIndex={activePageIndex}
            onPageSelect={(pageIndex) => {
              // Sync ref immediately so paste/place targets the clicked page before
              // the intersection observer catches an intermediate page mid-scroll.
              activePageIndexRef.current = pageIndex;
              setActivePageIndex(pageIndex);
              pdf.setStatusMessage(`Page ${pageIndex + 1}`);
            }}
            onMergeFileSelected={(f) => void pageOps.mergeFromFile(f)}
            onInsertBlank={(idx) => pageOps.insertBlank(idx ?? activePageIndex)}
            onDuplicatePage={(idx) => pageOps.duplicatePage(idx)}
            onDeletePage={(idx) => pageOps.deletePage(idx)}
            onRotatePage={(idx, direction) => pageOps.rotatePage(idx, direction)}
            onReorderPages={(order) => { void pageOps.reorderPages(order); }}
          />
          <input
            ref={imageFileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              handleImageFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />

        </>
      }
      inspector={
        <RightInspector
          selectedField={fields.selectedField}
          fields={fields.fields}
          savedSignatures={library.savedSignatures}
          hasPdf={hasPdf}
          isBusy={isBusy}
          onCreateDrawSignature={() => openSignatureMode('draw')}
          onCreateTypeSignature={() => openSignatureMode('type')}
          onCreateUploadSignature={() => openSignatureMode('upload')}
          signerProfile={profile}
          onProfileChange={(patch) => setProfile((prev) => ({ ...prev, ...patch }))}
          documentName={pdf.originalFileName}
          pageCount={pdf.pageCount}
          fileSize={pdf.fileSize || pdf.pdfFile?.size || 0}
          savedSignatureCount={library.savedSignatures.length}
          hasAcroFormFields={pdf.hasAcroFormFields}
          memoryTip={memoryTip}
          workflowStage={workflowStage}
          onFieldUpdate={fields.updateField}
          onApplyTextFormat={applyTextFormat}
          bulkFieldIds={inspectorBulkFieldIds}
          onBulkFieldIdsChange={setInspectorBulkFieldIds}
          onFieldDelete={fields.deleteField}
          onFieldSelect={handleFieldSelect}
          onPlaceSavedSignature={(signature) => {
            library.placeSavedSignature(signature);
            if (hasPdf) {
              armPendingPlacement(signature.label);
            } else {
              pdf.setStatusMessage('Upload a PDF first, then click the signature to place it.');
            }
          }}
          onRenameSavedSignature={library.renameSavedSignature}
          onDeleteSavedSignature={library.deleteSavedSignature}
          snapshots={snapshots}
          onLoadSnapshot={loadSnapshot}
          onDeleteSnapshot={deleteSnapshot}
          onCompareSnapshot={handleCompareSnapshot}
          onRenameSnapshot={renameSnapshot}
          comparingSnapshotId={compareSnapshotId}
          onReorderFields={fields.reorderFields}
          onReorderSnapshots={reorderSnapshots}
          onSetSnapshotGroup={setSnapshotGroup}
          onExportTemplate={exportFieldTemplate}
          onImportTemplate={() => templateImportRef.current?.click()}
          canExportTemplate={fields.fields.length > 0}
          onClearAllFields={() => fields.clearFields()} // Contract #2: single undo entry
        />
      }
      statusBar={
        <StatusBar
          message={pdf.statusMessage || 'Everything runs locally. Your PDFs never leave this computer.'}
          pageIndex={activePageIndex}
          pageCount={pdf.pageCount}
          zoomPercent={hasPdf ? viewerScale : undefined}
          fieldCount={fields.fields.length}
          isDirty={workflowDirty || pdf.isSignedOutputStale}
          fileIdPreview={pdf.uploadedFileId}
          shortcutsOpen={shortcutsOpen}
          onShortcutsOpenChange={setShortcutsOpen}
          onPageJump={(pageIndex) => {
            // Sync ref immediately so Ctrl+V paste targets the jumped page before
            // the intersection observer catches an intermediate page mid-scroll.
            activePageIndexRef.current = pageIndex;
            setActivePageIndex(pageIndex);
            const scrollRoot = document.querySelector('.editor-stage-viewer');
            const pageElement = document.getElementById(`pdf-page-${pageIndex}`);
            if (pageElement && scrollRoot) {
              const rootRect = scrollRoot.getBoundingClientRect();
              const pageRect = pageElement.getBoundingClientRect();
              scrollRoot.scrollTop += pageRect.top - rootRect.top - 12;
            } else {
              pageElement?.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
            pdf.setStatusMessage(`Page ${pageIndex + 1}`);
          }}
        />
      }
      banners={
        <>
      {activeErrorMessage && (
        <div
          role="alert"
          className="mx-4 mt-3 rounded-lg border border-state-danger-bg bg-state-danger-bg px-4 py-2 text-sm text-state-danger dark:border-state-danger/50 dark:bg-red-950/40 dark:text-state-danger-bg"
        >
          {activeErrorMessage}
          <button
            type="button"
            onClick={handleDismissError}
            aria-label="Dismiss error"
            className="float-right font-semibold"
          >
            Dismiss
          </button>
        </div>
      )}
        </>
      }
      modals={
      <>
      <input
        ref={templateImportRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void importFieldTemplate(f);
        }}
      />
      {/* Full-window drop target overlay (visible while dragging a file over the app) */}
      {isDragOver && (
        <div className="drop-overlay" aria-hidden="true">
          <div className="drop-overlay-card">
            <div className="drop-overlay-icon">📄</div>
            <div className="drop-overlay-title">Drop PDF to open</div>
            <div className="drop-overlay-sub">You can also paste a copied PDF with Ctrl+V</div>
          </div>
        </div>
      )}
      <SignatureCreationFlow
        mode={creationMode}
        profileName={profile.name}
        onClose={() => setCreationMode(null)}
        onError={(message) => pdf.setStatusMessage(message)}
        onUseOnce={(dataUrl, label) => {
          library.useOnce(dataUrl, label);
          if (hasPdf) {
            armPendingPlacement(label);
          } else {
            pdf.setStatusMessage(
              `Signature ready. Upload a PDF, then place it from the Signatures panel.`
            );
          }
        }}
        onSaveAndPlace={(dataUrl, label) => {
          library.saveAndPlace(dataUrl, label);
          if (hasPdf) {
            armPendingPlacement(label);
          } else {
            pdf.setStatusMessage(
              `Saved "${label}" to your library. Upload a PDF when you are ready to place it.`
            );
          }
        }}
      />
      </>
      }
    />
  );
};

function formatTodayDate(): string {
  return getDefaultValueForType('date', DEFAULT_PROFILE);
}
